/**
 * Aba "Preferências" da personalização de IA.
 *
 * Preferência é instrução DURÁVEL: entra sozinha em toda conversa, em qualquer
 * processo. Pode ter nascido aqui (MANUAL) ou da própria conversa, quando a IA
 * registrou o que o advogado pediu (IA_AUTO) — daí o selo, que existe para ele
 * revisar o que não digitou.
 */
import { Bot, Pencil, Sparkles, Trash2, X } from 'lucide-react';
import { useState } from 'react';

import type { IaPreferencia } from '@/api/iaPersonalizacao';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  useAtualizarPreferencia,
  useCriarPreferencia,
  useExcluirPreferencia,
  usePreferenciasIa,
} from '@/hooks/useIaPersonalizacao';
import { classifyError } from '@/lib/errors';

const MAX_CHARS = 1000;

export function PreferenciasTab() {
  const { data, isLoading, isError, error, refetch } = usePreferenciasIa();
  const criar = useCriarPreferencia();
  const atualizar = useAtualizarPreferencia();
  const excluir = useExcluirPreferencia();

  const [nova, setNova] = useState('');
  const [emEdicao, setEmEdicao] = useState<{ id: string; texto: string } | null>(null);
  const [confirmarExclusao, setConfirmarExclusao] = useState<IaPreferencia | null>(null);

  const preferencias = data?.preferencias ?? [];
  const ativas = preferencias.filter((p) => p.ativo).length;
  const limite = data?.limite_ativas ?? 0;

  function adicionar() {
    const texto = nova.trim();
    if (!texto) return;
    criar.mutate(texto, { onSuccess: () => setNova('') });
  }

  function salvarEdicao() {
    if (!emEdicao) return;
    const texto = emEdicao.texto.trim();
    if (!texto) return;
    atualizar.mutate({ id: emEdicao.id, texto }, { onSuccess: () => setEmEdicao(null) });
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size={32} label="Carregando preferências" />
      </div>
    );
  }

  if (isError) {
    const { message } = classifyError(error);
    return (
      <ErrorState
        title="Não foi possível carregar suas preferências"
        description={message}
        actions={[{ label: 'Tentar novamente', onClick: () => refetch() }]}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Alert>
        <Sparkles className="h-4 w-4" />
        <AlertDescription>
          Instruções que a IA segue em <strong>todas</strong> as conversas, em qualquer processo — estilo de
          redação, regras que você sempre aplica, entendimentos que adota. Durante uma conversa, basta pedir
          (&ldquo;sempre faça assim&rdquo;) que ela registra aqui para você conferir.
        </AlertDescription>
      </Alert>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <Textarea
            value={nova}
            onChange={(e) => setNova(e.target.value.slice(0, MAX_CHARS))}
            placeholder="Ex.: Prefiro fundamentação concisa, sem citação de doutrina."
            rows={3}
            aria-label="Nova preferência"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {ativas} de {limite} preferências ativas
            </span>
            <Button onClick={adicionar} disabled={!nova.trim() || criar.isPending}>
              {criar.isPending && <Spinner className="mr-2 text-inherit" />}
              Adicionar preferência
            </Button>
          </div>
        </CardContent>
      </Card>

      {preferencias.length === 0 ? (
        <EmptyState
          icon={<Sparkles />}
          title="Nenhuma preferência cadastrada"
          description="Cadastre instruções que valem para todas as suas conversas com a IA."
        />
      ) : (
        <ul className="space-y-2">
          {preferencias.map((p) => (
            <li key={p.id}>
              <Card className={p.ativo ? undefined : 'opacity-60'}>
                <CardContent className="flex items-start gap-3 py-4">
                  <Switch
                    checked={p.ativo}
                    onCheckedChange={(ativo) => atualizar.mutate({ id: p.id, ativo })}
                    aria-label={p.ativo ? 'Desativar preferência' : 'Ativar preferência'}
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    {emEdicao?.id === p.id ? (
                      <>
                        <Textarea
                          value={emEdicao.texto}
                          onChange={(e) => setEmEdicao({ id: p.id, texto: e.target.value.slice(0, MAX_CHARS) })}
                          rows={3}
                          aria-label="Editar preferência"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={salvarEdicao} disabled={atualizar.isPending}>
                            Salvar
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEmEdicao(null)}>
                            <X className="mr-1 h-4 w-4" />
                            Cancelar
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="whitespace-pre-wrap break-words text-sm text-foreground">{p.texto}</p>
                        {p.origem === 'IA_AUTO' && (
                          <Badge variant="secondary" className="gap-1">
                            <Bot className="h-3 w-3" />
                            Registrada pela IA
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                  {emEdicao?.id !== p.id && (
                    <div className="flex shrink-0 gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Editar preferência"
                        onClick={() => setEmEdicao({ id: p.id, texto: p.texto })}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Excluir preferência"
                        onClick={() => setConfirmarExclusao(p)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={confirmarExclusao !== null}
        title="Excluir preferência"
        message="A IA deixa de considerá-la nas próximas conversas. Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        confirmColor="error"
        loading={excluir.isPending}
        onCancel={() => setConfirmarExclusao(null)}
        onConfirm={() => {
          if (!confirmarExclusao) return;
          excluir.mutate(confirmarExclusao.id, { onSuccess: () => setConfirmarExclusao(null) });
        }}
      />
    </div>
  );
}
